import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { mockVendorManagementService } from '@/services/mock/MockVendorManagementService';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Users, ArrowLeft, AlertTriangle } from 'lucide-react';
import { ROUTES } from '@/constants/routes';

export const VendorCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2 | 3 | 4 | 5>(1);

  // Form State
  const [name, setName] = useState('');
  const [legalName, setLegalName] = useState('');
  const [vendorType, setVendorType] = useState('OEM Manufacturer');
  const [category, setCategory] = useState('IT Hardware');
  const [industry] = useState('Information Technology');

  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country] = useState('India');

  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactRole, setContactRole] = useState('Sales Director');

  const [gstNumber, setGstNumber] = useState('');
  const [panNumber, setPanNumber] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('Net 45 Days post GRN');

  const [duplicateMatches, setDuplicateMatches] = useState<any[]>([]);
  const [isScreening, setIsScreening] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRunDuplicateCheck = async () => {
    if (!name) {
      setError('Vendor Name is required before running screening.');
      return;
    }
    try {
      setIsScreening(true);
      setError(null);
      const res = await mockVendorManagementService.checkDuplicateVendor(name);
      setDuplicateMatches(res.matches);
      setStep(5);
    } finally {
      setIsScreening(false);
    }
  };

  const handleSubmit = async () => {
    if (!name || !legalName || !contactEmail) {
      setError('Name, Legal Name, and Primary Contact Email are required.');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const created = await mockVendorManagementService.createVendor({
        name,
        legalName,
        vendorType,
        category,
        industry,
        status: 'PENDING_REVIEW',
        verificationStatus: 'PENDING',
        riskRating: 'LOW',
        primaryContact: {
          id: 'CNT-NEW-01',
          name: contactName || 'Primary Representative',
          role: contactRole,
          email: contactEmail,
          phone: contactPhone || '+91 99000 11223',
          isPrimary: true,
          status: 'ACTIVE',
        },
        contacts: [],
        address: {
          street: street || '101 Industrial Tech Park',
          city: city || 'Noida',
          state: state || 'Uttar Pradesh',
          country,
          postalCode: postalCode || '201301',
        },
        commercial: {
          currency: 'INR',
          paymentTerms,
          incoterms: 'DDP',
          gstNumber: gstNumber || 'GSTIN-09AAAAA9999A1Z1',
          panNumber: panNumber || 'AAAAA9999A',
          maskedBankAccount: '**** **** 8821',
          bankName: 'HDFC Bank Ltd',
          preferredTier: 'STANDARD',
        },
        compliance: [
          { id: 'CMP-NEW-1', documentType: 'GST Registration Certificate', documentNumber: gstNumber || 'GSTIN-NEW', issuedDate: '2024-01-01', expiryDate: '2027-01-01', daysRemaining: 365, status: 'VERIFIED' },
        ],
        performance: {
          performanceScore: 85,
          onTimeDeliveryRate: 90,
          qualityIndex: 92,
          rfqsParticipated: 0,
          bidsSubmitted: 0,
          ordersCompleted: 0,
        },
        rfqHistory: [],
      });

      navigate(`/procurement/vendors/${created.id}`);
    } catch (err: any) {
      setError(err.message || 'Failed to onboard vendor record');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 pb-24 font-poppins max-w-4xl mx-auto">
      {/* Top Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(ROUTES.VENDORS.LIST)}
            className="h-9 rounded-xl border-border font-bold"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Vendor Directory
          </Button>

          <h1 className="text-2xl font-extrabold tracking-tight text-foreground flex items-center gap-2">
            <Users className="h-6 w-6 text-primary" /> Vendor Onboarding Master Form
          </h1>
        </div>
      </div>

      {/* Stepper Bar */}
      <div className="grid grid-cols-5 gap-2 text-xs font-bold">
        {[
          { num: 1, label: '1. Basic Info' },
          { num: 2, label: '2. Address & Org' },
          { num: 3, label: '3. Contacts' },
          { num: 4, label: '4. Commercial' },
          { num: 5, label: '5. Review & Submit' },
        ].map((s) => (
          <div
            key={s.num}
            onClick={() => setStep(s.num as any)}
            className={`p-3 rounded-xl border text-center cursor-pointer transition-all ${
              step === s.num
                ? 'border-primary bg-primary text-white shadow-2xs'
                : step > s.num
                ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700'
                : 'border-border bg-card text-foreground-secondary'
            }`}
          >
            {s.label}
          </div>
        ))}
      </div>

      {error && (
        <div className="p-3 rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-700 font-bold text-xs">
          {error}
        </div>
      )}

      {/* STEP 1: BASIC INFO */}
      {step === 1 && (
        <Card className="border-border rounded-2xl shadow-2xs p-6 space-y-4 text-xs">
          <div className="space-y-1">
            <label className="font-bold text-foreground block">Supplier Trade Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Cisco Systems Enterprise India"
              className="w-full rounded-xl border border-border bg-background px-3 py-2.5 font-bold text-foreground"
              required
            />
          </div>

          <div className="space-y-1">
            <label className="font-bold text-foreground block">Legal Registered Name *</label>
            <input
              type="text"
              value={legalName}
              onChange={(e) => setLegalName(e.target.value)}
              placeholder="e.g. Cisco Systems India Private Limited"
              className="w-full rounded-xl border border-border bg-background px-3 py-2.5 font-bold text-foreground"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-bold text-foreground block mb-1">Procurement Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full rounded-xl border border-border bg-background px-3 py-2 font-bold text-foreground"
              >
                <option value="IT Hardware">IT Hardware</option>
                <option value="Facilities & Maintenance">Facilities & Maintenance</option>
                <option value="IT Services">IT Services</option>
              </select>
            </div>

            <div>
              <label className="font-bold text-foreground block mb-1">Vendor Type</label>
              <select
                value={vendorType}
                onChange={(e) => setVendorType(e.target.value)}
                className="w-full rounded-xl border border-border bg-background px-3 py-2 font-bold text-foreground"
              >
                <option value="OEM Manufacturer">OEM Manufacturer</option>
                <option value="Authorized Distributor">Authorized Distributor</option>
                <option value="Service Provider">Service Provider</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t border-border">
            <Button onClick={() => setStep(2)} className="rounded-xl font-bold bg-primary text-white">
              Next: Address & Org →
            </Button>
          </div>
        </Card>
      )}

      {/* STEP 2: ADDRESS & ORG */}
      {step === 2 && (
        <Card className="border-border rounded-2xl shadow-2xs p-6 space-y-4 text-xs">
          <div className="space-y-1">
            <label className="font-bold text-foreground block">Registered Street Address</label>
            <input
              type="text"
              value={street}
              onChange={(e) => setStreet(e.target.value)}
              placeholder="Building, Street, Sector"
              className="w-full rounded-xl border border-border bg-background px-3 py-2.5 font-bold text-foreground"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="font-bold text-foreground block mb-1">City</label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Noida"
                className="w-full rounded-xl border border-border bg-background px-3 py-2 font-bold text-foreground"
              />
            </div>

            <div>
              <label className="font-bold text-foreground block mb-1">State</label>
              <input
                type="text"
                value={state}
                onChange={(e) => setState(e.target.value)}
                placeholder="Uttar Pradesh"
                className="w-full rounded-xl border border-border bg-background px-3 py-2 font-bold text-foreground"
              />
            </div>

            <div>
              <label className="font-bold text-foreground block mb-1">Postal Code</label>
              <input
                type="text"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                placeholder="201301"
                className="w-full rounded-xl border border-border bg-background px-3 py-2 font-mono font-bold text-foreground"
              />
            </div>
          </div>

          <div className="flex justify-between pt-4 border-t border-border">
            <Button variant="outline" onClick={() => setStep(1)} className="rounded-xl font-bold">
              ← Back
            </Button>
            <Button onClick={() => setStep(3)} className="rounded-xl font-bold bg-primary text-white">
              Next: Contacts →
            </Button>
          </div>
        </Card>
      )}

      {/* STEP 3: CONTACTS */}
      {step === 3 && (
        <Card className="border-border rounded-2xl shadow-2xs p-6 space-y-4 text-xs">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-bold text-foreground block mb-1">Primary Representative Name *</label>
              <input
                type="text"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                placeholder="Rajesh Kumar"
                className="w-full rounded-xl border border-border bg-background px-3 py-2 font-bold text-foreground"
                required
              />
            </div>

            <div>
              <label className="font-bold text-foreground block mb-1">Contact Email *</label>
              <input
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="bids@cisco.com"
                className="w-full rounded-xl border border-border bg-background px-3 py-2 font-bold text-foreground"
                required
              />
            </div>

            <div>
              <label className="font-bold text-foreground block mb-1">Contact Phone</label>
              <input
                type="text"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                placeholder="+91 98110 00000"
                className="w-full rounded-xl border border-border bg-background px-3 py-2 font-bold text-foreground"
              />
            </div>

            <div>
              <label className="font-bold text-foreground block mb-1">Role / Designation</label>
              <input
                type="text"
                value={contactRole}
                onChange={(e) => setContactRole(e.target.value)}
                placeholder="Sales Director"
                className="w-full rounded-xl border border-border bg-background px-3 py-2 font-bold text-foreground"
              />
            </div>
          </div>

          <div className="flex justify-between pt-4 border-t border-border">
            <Button variant="outline" onClick={() => setStep(2)} className="rounded-xl font-bold">
              ← Back
            </Button>
            <Button onClick={() => setStep(4)} className="rounded-xl font-bold bg-primary text-white">
              Next: Commercial →
            </Button>
          </div>
        </Card>
      )}

      {/* STEP 4: COMMERCIAL */}
      {step === 4 && (
        <Card className="border-border rounded-2xl shadow-2xs p-6 space-y-4 text-xs">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-bold text-foreground block mb-1">GST Registration Number</label>
              <input
                type="text"
                value={gstNumber}
                onChange={(e) => setGstNumber(e.target.value)}
                placeholder="GSTIN-07AAAAA0000A1Z5"
                className="w-full rounded-xl border border-border bg-background px-3 py-2 font-mono font-bold text-foreground"
              />
            </div>

            <div>
              <label className="font-bold text-foreground block mb-1">PAN Identifier</label>
              <input
                type="text"
                value={panNumber}
                onChange={(e) => setPanNumber(e.target.value)}
                placeholder="AAAAA0000A"
                className="w-full rounded-xl border border-border bg-background px-3 py-2 font-mono font-bold text-foreground"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="font-bold text-foreground block">Payment Terms</label>
            <input
              type="text"
              value={paymentTerms}
              onChange={(e) => setPaymentTerms(e.target.value)}
              placeholder="Net 45 Days post GRN inspection"
              className="w-full rounded-xl border border-border bg-background px-3 py-2.5 font-bold text-foreground"
            />
          </div>

          <div className="flex justify-between pt-4 border-t border-border">
            <Button variant="outline" onClick={() => setStep(3)} className="rounded-xl font-bold">
              ← Back
            </Button>
            <Button
              disabled={isScreening}
              onClick={handleRunDuplicateCheck}
              className="rounded-xl font-bold bg-primary text-white shadow-2xs"
            >
              {isScreening ? 'Screening Duplicates...' : 'Run Duplicate Check →'}
            </Button>
          </div>
        </Card>
      )}

      {/* STEP 5: REVIEW & SUBMIT */}
      {step === 5 && (
        <Card className="border-border rounded-2xl shadow-2xs p-6 space-y-4 text-xs">
          <h3 className="font-bold text-sm text-foreground pb-2 border-b border-border">
            Duplicate Screening & Master Review
          </h3>

          {duplicateMatches.length > 0 ? (
            <div className="p-4 rounded-xl border border-amber-500/30 bg-amber-500/10 text-amber-800 space-y-2">
              <div className="flex items-center gap-2 font-bold">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                <span>Possible Duplicate Vendor Records Detected:</span>
              </div>

              {duplicateMatches.map((m) => (
                <div key={m.id} className="p-2 rounded border border-amber-500/20 bg-card font-mono text-[11px]">
                  <strong>{m.vendorCode}</strong> - {m.name} ({m.legalName})
                </div>
              ))}
            </div>
          ) : (
            <div className="p-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 text-emerald-700 font-bold">
              ✓ Duplicate Screening Passed: No matching vendor records found in master directory.
            </div>
          )}

          <div className="space-y-2 pt-2">
            <p>Trade Name: <strong className="text-foreground">{name}</strong></p>
            <p>Legal Name: <strong className="text-foreground">{legalName}</strong></p>
            <p>Primary Contact: <strong className="text-foreground">{contactName} ({contactEmail})</strong></p>
          </div>

          <div className="flex justify-between pt-4 border-t border-border">
            <Button variant="outline" onClick={() => setStep(4)} className="rounded-xl font-bold">
              ← Back to Terms
            </Button>
            <Button
              disabled={isSubmitting}
              onClick={handleSubmit}
              className="rounded-xl font-bold bg-primary text-white shadow-2xs"
            >
              {isSubmitting ? 'Onboarding Vendor...' : 'Onboard Vendor Master'}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};
